import type { ThemeConfig } from 'antd';
import { govardDarkTheme, govardLightTheme } from './antdTheme.js';

/**
 * Theme variable bridge.
 *
 * antd v6 cssVar mode attaches `--ant-*` variables to hashed per-component
 * classes, so raw `var(--ant-…)` outside antd elements (shell CSS, Tailwind
 * arbitrary values, inline styles) never resolves. The shared
 * `@govard/ui-primitives` token sheet defines `--color-*` on `:root` but only
 * for the light palette.
 *
 * This module injects one static stylesheet that publishes:
 *  - `:root` — semantic `--color-*` aliases pointing at `--ant-*` tokens, so
 *    ui-primitives consumers follow the antd Warm Precision palette in both
 *    themes without duplicating hex values;
 *  - `:root[data-theme='light']` — every `--ant-*` token from the light theme;
 *  - `:root[data-theme='dark']` — dark `--ant-*` tokens plus dark overrides
 *    for the palette scales that have no antd counterpart (brand, neutrals,
 *    shadows) and a few legacy literals.
 *
 * The sheet is static: theme switching is driven by the `data-theme`
 * attribute on <html>, so no re-injection happens on toggle. Alias blocks
 * resolve their `var()` references lazily, so they re-resolve per theme.
 */

const STYLE_ID = 'govard-theme-vars';

/** Semantic `--color-*` → `--ant-*` aliases shared by both themes. */
const SEMANTIC_ALIASES: Record<string, string> = {
  // brand / status
  '--color-primary': 'var(--ant-color-primary)',
  '--color-primary-hover': 'var(--ant-color-primary-hover)',
  '--color-primary-light': 'var(--ant-color-primary-bg)',
  '--color-primary-muted': 'rgba(217, 119, 6, 0.1)',
  '--color-ring': 'var(--ant-color-primary-border-hover)',
  '--color-success': 'var(--ant-color-success)',
  '--color-success-light': 'var(--ant-color-success-bg)',
  '--color-warning': 'var(--ant-color-warning)',
  '--color-warning-light': 'var(--ant-color-warning-bg)',
  '--color-error': 'var(--ant-color-error)',
  '--color-error-light': 'var(--ant-color-error-bg)',
  '--color-destructive': 'var(--ant-color-error)',
  '--color-info': 'var(--ant-color-info)',
  '--color-info-light': 'var(--ant-color-info-bg)',
  // text
  '--color-foreground': 'var(--ant-color-text)',
  '--color-text': 'var(--ant-color-text)',
  '--color-text-secondary': 'var(--ant-color-text-secondary)',
  '--color-text-muted': 'var(--ant-color-text-tertiary)',
  '--color-muted-foreground': 'var(--ant-color-text-tertiary)',
  '--color-muted-fg': 'var(--ant-color-text-tertiary)',
  // surfaces
  '--color-background': 'var(--ant-color-bg-layout)',
  '--color-bg': 'var(--ant-color-bg-layout)',
  '--color-surface': 'var(--ant-color-bg-container)',
  '--color-card': 'var(--ant-color-bg-container)',
  '--color-card-foreground': 'var(--ant-color-text)',
  '--color-popover': 'var(--ant-color-bg-elevated)',
  '--color-popover-foreground': 'var(--ant-color-text)',
  '--color-muted': 'var(--ant-color-bg-container)',
  '--color-accent-bg': 'var(--ant-color-primary-bg)',
  // borders
  '--color-border': 'var(--ant-color-border-secondary)',
  '--color-border-hover': 'var(--ant-color-border)',
};

function kebab(key: string): string {
  return key.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
}

/** Tokens whose numeric value is not a length (px). */
const UNITLESS_KEYS = new Set(['lineheight', 'fontweight', 'opacity', 'zindex']);
const MS_KEYS = /^(motion-duration|duration)/;

function formatTokenValue(key: string, value: number): string {
  const kebabKey = kebab(key);
  if (UNITLESS_KEYS.has(kebabKey)) return `${value}`;
  if (MS_KEYS.test(kebabKey)) return `${value}ms`;
  return `${value}px`;
}

function tokenVars(theme: ThemeConfig): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(theme.token ?? {})) {
    if (typeof value === 'number') out[`--ant-${kebab(key)}`] = formatTokenValue(key, value);
    else if (typeof value === 'string') out[`--ant-${kebab(key)}`] = value;
  }
  return out;
}

/** Dark overrides for palette scales without an antd counterpart (spec D dark). */
const DARK_COLOR_OVERRIDES: Record<string, string> = {
  // surfaces
  '--color-bg-secondary': '#1c1917',
  '--color-surface-hover': '#383533',
  '--color-surface-active': '#44403c',
  // brand / primary scale
  '--color-primary-hover': '#fbbf24',
  '--color-primary-muted': 'rgb(217 119 6 / 0.22)',
  '--color-brand-50': '#2e1e0a',
  '--color-brand-100': '#452b0f',
  '--color-brand-500': '#f59e0b',
  '--color-brand-600': '#f59e0b',
  '--color-brand-700': '#fbbf24',
  '--color-brand-900': '#fde68a',
  // neutrals
  '--color-neutral-50': '#383533',
  '--color-neutral-100': '#292524',
  '--color-neutral-500': '#a8a29e',
  '--color-neutral-900': '#f5f5f4',
  '--color-neutral-950': '#fafaf9',
  // accent (operator blue)
  '--color-accent-500': '#60a5fa',
  '--color-accent-600': '#3b82f6',
  // status scales
  '--color-success-500': '#4ade80',
  '--color-warning-500': '#fbbf24',
  '--color-danger-500': '#f87171',
  '--color-danger-600': '#f87171',
  '--color-error': '#f87171',
  '--color-destructive': '#f87171',
  '--color-info-500': '#60a5fa',
  '--color-info-600': '#3b82f6',
  // shadows: warm-brown umbra does not read on dark surfaces
  '--shadow-xs': '0 1px 2px rgb(0 0 0 / 0.3)',
  '--shadow-sm': '0 1px 3px rgb(0 0 0 / 0.35), 0 1px 2px rgb(0 0 0 / 0.3)',
  '--shadow-md': '0 4px 8px -2px rgb(0 0 0 / 0.35), 0 2px 4px -2px rgb(0 0 0 / 0.3)',
  '--shadow-lg': '0 12px 24px -4px rgb(0 0 0 / 0.4), 0 4px 8px -2px rgb(0 0 0 / 0.3)',
};

/** Extra aliases used by the hex→var codemod; defined for both themes. */
const LIGHT_EXTRA: Record<string, string> = {
  '--g-fill': '#f5f1eb',
  '--g-fill-strong': '#e7e5e4',
  '--color-surface-hover': '#f5f1eb',
  '--color-surface-active': '#ede9e3',
  '--crm-sider-bg': '#f5f1eb',
  // Solid on-accent text: light CTA colors are dark enough for white (AA 5:1+).
  '--crm-on-accent': '#ffffff',
  '--crm-on-danger': '#ffffff',
  '--crm-on-warn': '#ffffff',
};

const DARK_EXTRA: Record<string, string> = {
  '--g-fill': '#292524',
  '--g-fill-strong': '#44403c',
  '--crm-sider-bg': '#1c1917',
  // Spec --on-accent: solid chips flip to dark text on bright amber
  // (white on #f59e0b is 2.15:1 — fails AA; #241a05 passes at 7.9:1).
  '--crm-on-accent': '#241a05',
  '--crm-on-danger': '#450a0a',
  '--crm-on-warn': '#431407',
};

function renderBlock(selector: string, vars: Record<string, string>): string {
  const body = Object.entries(vars)
    .map(([name, value]) => `  ${name}: ${value};`)
    .join('\n');
  return `${selector} {\n${body}\n}`;
}

export function buildThemeVarsCss(): string {
  return [
    renderBlock(':root', SEMANTIC_ALIASES),
    renderBlock(":root[data-theme='light']", { ...tokenVars(govardLightTheme), ...LIGHT_EXTRA }),
    renderBlock(":root[data-theme='dark']", {
      ...tokenVars(govardDarkTheme),
      ...DARK_COLOR_OVERRIDES,
      ...DARK_EXTRA,
    }),
  ].join('\n\n');
}

/** Inject the static bridge stylesheet once. Safe to call repeatedly. */
export function ensureThemeVarsInjected(doc: Document = document): void {
  if (typeof doc === 'undefined') return;
  if (doc.getElementById(STYLE_ID)) return;
  const style = doc.createElement('style');
  style.id = STYLE_ID;
  style.textContent = buildThemeVarsCss();
  doc.head.appendChild(style);
}
